import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";

// #276 (UC-1/2/4): Die drei Erstell-Masken. Anders als „Marketplace einbinden" (#232,
// registriert einen ZEIGER auf eine fremde Quelle) legen diese Aufrufe ein eigenes Repo in
// unserem Gitea an und befüllen es mit gültiger Struktur.

export interface ScaffoldMarketplaceInput {
  name: string;
  repoName?: string;
  description?: string;
  scope?: string;
}

export interface ScaffoldMarketplaceResult {
  marketplaceId: string;
  managedRepoId: string;
  repoFullName: string;
  branch: string;
}

export interface ScaffoldPluginInput {
  marketplaceId: string;
  name: string;
  description?: string;
  placement: "in-marketplace" | "own-repo";
  repoName?: string;
}

export interface ScaffoldPluginResult {
  marketplaceId: string;
  managedRepoId?: string;
  repoFullName?: string;
  /** Der Entwurf, in dem der Katalog-Eintrag liegt — das Plugin ist NICHT live. */
  draftBranch: string;
  pullNumber: number | null;
}

export interface ForkTemplateInput {
  repoName: string;
  from: { kind: "seed"; source: string } | { kind: "template"; repoId: string; branch?: string };
  description?: string;
  profile?: { mode: "none" | "attach" | "create"; profileId?: string; name?: string };
}

export interface ForkTemplateResult {
  managedRepoId: string;
  repoFullName: string;
  branch: string;
  profileId?: string;
}

function useClient() {
  const { session, logout } = useAuth();
  return createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

export function useScaffoldMarketplace(): UseMutationResult<
  ScaffoldMarketplaceResult,
  Error,
  ScaffoldMarketplaceInput
> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => client.post<ScaffoldMarketplaceResult>("/git-content/marketplaces", input),
    onSuccess: () => {
      // Ein Scaffold erzeugt BEIDES: einen Katalog-Eintrag und ein managebares Repo.
      void qc.invalidateQueries({ queryKey: queryKeys.resource("marketplaces") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("plugins") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("git-content") });
    },
  });
}

export function useScaffoldPlugin(): UseMutationResult<ScaffoldPluginResult, Error, ScaffoldPluginInput> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => client.post<ScaffoldPluginResult>("/git-content/plugins", input),
    onSuccess: () => {
      // `git-content` deckt Repos, Baum, Dateien UND die Entwurfs-Listen ab (alle unter
      // demselben Key-Präfix) — der neue Entwurf wird also sichtbar. Der LIVE-Katalog
      // ändert sich noch nicht; er wird trotzdem mitgezogen, weil die Freigabe direkt
      // folgen kann und ein veralteter Katalog dann die falsche Geschichte erzählt.
      void qc.invalidateQueries({ queryKey: queryKeys.resource("git-content") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("plugins") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("marketplaces") });
    },
  });
}

export function useForkTemplate(): UseMutationResult<ForkTemplateResult, Error, ForkTemplateInput> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => client.post<ForkTemplateResult>("/git-content/templates", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.resource("git-content") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("settingsProfiles") });
      void qc.invalidateQueries({ queryKey: ["gitea"] });
    },
  });
}
